/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { FocusZone, FocusZoneDirection } from '@fluentui/react/lib/FocusZone';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';

import { CreateTemplateInstanceWizardSteps } from '../../../apollo/reactiveVariables/TemplateInstanceVariables';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { getIdSeletorByCurrentStep } from '../../../utilities/TemplateUtils';
import { WizardStepLabel, WizardStepLabelProps } from '../WizardStepLabel';
import { getStackTokens } from './CreateTemplateInstanceSidebar.styles';

export interface CreateTemplateInstanceSidebarComponentProps {
    currentStep: CreateTemplateInstanceWizardSteps;
    enabledSteps: Set<CreateTemplateInstanceWizardSteps>;
    onStepClick?: (step: CreateTemplateInstanceWizardSteps) => void;
}
export const CreateTemplateInstanceSidebarComponent: React.FC<CreateTemplateInstanceSidebarComponentProps> = (
    props: CreateTemplateInstanceSidebarComponentProps
): JSX.Element => {
    const loc = getAppHost().getLocale();
    const { currentStep, enabledSteps, onStepClick } = props;

    const stepComponents = React.useMemo((): JSX.Element[] => {
        const steps: WizardStepLabelProps[] = [
            {
                name: loc.strings.basic_settings_wizard_step_label,
                label: '1',
                stepKey: CreateTemplateInstanceWizardSteps.BasicSettings,
            },
            {
                name: loc.strings.form_design_wizard_step_label,
                label: '2',
                stepKey: CreateTemplateInstanceWizardSteps.FormDesign,
            },
            {
                name: loc.strings.workflow_settings_wizard_step_label,
                label: '3',
                stepKey: CreateTemplateInstanceWizardSteps.WorkflowSettings,
            },
        ];

        return steps.map((stepProps: WizardStepLabelProps, index: number): JSX.Element => {
            stepProps.isCurrent = currentStep === stepProps.stepKey;
            stepProps.isDisabled = !enabledSteps.has(stepProps.stepKey);
            stepProps.onClick = onStepClick;
            // set arai-level to index+1 because aria level is 1-based index
            return <WizardStepLabel {...stepProps} key={index} ariaLevel={index + 1} />;
        });
    }, [currentStep, enabledSteps, onStepClick]);

    return (
        <FocusZone
            direction={FocusZoneDirection.vertical}
            defaultTabbableElement={getIdSeletorByCurrentStep(currentStep)}
            key={`Sidebar${currentStep}`}>
            <Stack tokens={getStackTokens} role='tablist'>
                {stepComponents}
            </Stack>
        </FocusZone>
    );
};
